import { useCallback, useEffect, useRef, useState } from "react";
import { COMMENTARY_CLIP } from "./commentaryBoothData";

function formatAudioClock(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/**
 * Modular commentary audio controller.
 * Uses HTMLAudioElement when `clip.src` is set; falls back to a timed mock
 * if the asset is missing or fails to load — swap COMMENTARY_CLIP.src later.
 *
 * @param {typeof COMMENTARY_CLIP} [clip]
 */
export function useCommentaryAudio(clip = COMMENTARY_CLIP) {
  const audioRef = useRef(null);
  const mockTimerRef = useRef(null);
  const mockPosRef = useRef(0);
  const usingMockRef = useRef(!clip?.src);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(clip?.mockDurationSec || 8);
  const [loadError, setLoadError] = useState("");

  const clearMockTimer = useCallback(() => {
    if (mockTimerRef.current) {
      window.clearInterval(mockTimerRef.current);
      mockTimerRef.current = null;
    }
  }, []);

  const stopMock = useCallback(() => {
    clearMockTimer();
    setIsPlaying(false);
  }, [clearMockTimer]);

  const startMock = useCallback(() => {
    clearMockTimer();
    usingMockRef.current = true;
    const total = clip?.mockDurationSec || 8;
    setDuration(total);
    setIsPlaying(true);
    setHasPlayedOnce(true);

    mockTimerRef.current = window.setInterval(() => {
      mockPosRef.current += 0.25;
      if (mockPosRef.current >= total) {
        mockPosRef.current = total;
        setCurrentTime(total);
        stopMock();
        return;
      }
      setCurrentTime(mockPosRef.current);
    }, 250);
  }, [clip?.mockDurationSec, clearMockTimer, stopMock]);

  useEffect(() => {
    const src = String(clip?.src || "").trim();
    if (!src) {
      usingMockRef.current = true;
      setDuration(clip?.mockDurationSec || 8);
      return undefined;
    }

    const audio = new Audio();
    audio.preload = "auto";
    // Do not set crossOrigin — S3 may not send CORS headers; playback does not need it.
    audio.src = src;
    audio.load();
    audioRef.current = audio;
    usingMockRef.current = false;
    setLoadError("");

    const onLoaded = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onPlay = () => {
      setIsPlaying(true);
      setHasPlayedOnce(true);
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration || 0);
    };
    const onError = () => {
      console.warn("Commentary clip failed to load:", src);
      setLoadError("Clip unavailable — using booth mock signal.");
      usingMockRef.current = true;
      setDuration(clip?.mockDurationSec || 8);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("canplaythrough", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("canplaythrough", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
      clearMockTimer();
    };
  }, [clip?.src, clip?.mockDurationSec, clearMockTimer]);

  const play = useCallback(async () => {
    if (usingMockRef.current || !audioRef.current) {
      if (mockPosRef.current >= (clip?.mockDurationSec || 8)) {
        mockPosRef.current = 0;
        setCurrentTime(0);
      }
      startMock();
      return;
    }
    try {
      await audioRef.current.play();
    } catch {
      setLoadError("Playback blocked — using booth mock signal.");
      startMock();
    }
  }, [clip?.mockDurationSec, startMock]);

  const pause = useCallback(() => {
    if (usingMockRef.current) {
      stopMock();
      return;
    }
    audioRef.current?.pause();
  }, [stopMock]);

  const replay = useCallback(async () => {
    if (usingMockRef.current || !audioRef.current) {
      mockPosRef.current = 0;
      setCurrentTime(0);
      startMock();
      return;
    }
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTime(0);
    try {
      await audio.play();
    } catch {
      startMock();
    }
  }, [startMock]);

  const progress =
    duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;

  return {
    isPlaying,
    hasPlayedOnce,
    currentTime,
    duration,
    progress,
    timeLabel: `${formatAudioClock(currentTime)} / ${formatAudioClock(duration)}`,
    loadError,
    clipLabel: clip?.label || "Commentary clip",
    play,
    pause,
    replay,
  };
}
